import type { ReactNode } from "react";
import { cn } from "@/lib/utils/cn";

export type InfoBannerTone = "you" | "partner" | "shared" | "paper-deep";

export interface InfoBannerProps {
  tone?: InfoBannerTone;
  icon?: ReactNode;
  children: ReactNode;
  className?: string;
}

/**
 * P3 — InfoBanner.
 *
 * Per `component-api.md §2 P3`:
 *   - 4-tone横条: you / partner / shared / paper-deep
 *   - 用于持续性上下文信息 (不是错误、不是 toast)
 *   - 不带关闭按钮 (不弹)
 *   - 高度 ~44px (banner slot, per DESIGN §10 页面槽位约定)
 */
export function InfoBanner({ tone = "paper-deep", icon, children, className }: InfoBannerProps) {
  return (
    <div
      data-component="InfoBanner"
      data-tone={tone}
      className={cn(
        "min-h-[44px] flex items-center gap-3 px-4",
        // W41.0 §5.2: `border-hairline` → `shadow-card`. A banner now sits on the
        // L0 mist rather than flat paper, and a 1px line on a gradient reads dirty.
        // The four tones are unchanged — they are *tints*, and a tint is a
        // background, not a text colour (DESIGN.md §2.6); the text stays ink-1
        // or ink-2, which is the D11 two-step rule.
        "rounded-md shadow-card",
        tone === "you" && "bg-you-tint text-ink-1",
        tone === "partner" && "bg-partner-tint text-ink-1",
        tone === "shared" && "bg-shared-tint text-ink-1",
        tone === "paper-deep" && "bg-paper-deep text-ink-2",
        className,
      )}
    >
      {icon ? (
        <span className="shrink-0 text-ink-2" aria-hidden>
          {icon}
        </span>
      ) : null}
      <div className="flex-1 min-w-0 text-body-sm">{children}</div>
    </div>
  );
}
